import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../../redux/store";
import { costCenterTyped } from "../../../../../redux/slice/bookingCreation/bookingCreationSlice";
import { useCoveringOrganizations } from "../../hooks/useCoveredBooking";

/** Mirrors the backend limit (`COST_CENTER_MAX_LENGTH`); it refuses longer. */
const COST_CENTER_MAX_LENGTH = 100;

/**
 * The invoice recipient's cost centre on a covered booking (#1810, ADR-0012
 * amendment 2026-10-04) — the organisation's own posting reference, printed on
 * the invoice it receives.
 *
 * **Prefilled, not decided here.** The field shows the default the member's
 * profile carries (their unit's, else the organisation's). Untouched, the app
 * sends nothing and the server stores that same default; only a value the
 * member types is sent. Rendered by the summary only on a covered booking —
 * a guest's free text in a public body's posting reference is not something
 * anyone checks.
 */
const CoveredCostCenterField: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const organizations = useCoveringOrganizations();
  const selectedId = useSelector(
    (state: RootState) => state.bookingCreation.orderData.coveringOrganizationId
  );
  const typed = useSelector(
    (state: RootState) => state.bookingCreation.orderData.costCenter
  );

  // The organisation the booking is attributed to: the one picked, or the
  // only one there is. Same reading as CoveringOrganizationSelect.
  const covering =
    organizations.find((organization) => organization.id === selectedId) ??
    organizations[0];
  const value = typed ?? covering?.defaultCostCenter ?? "";

  return (
    <div className="space-y-1">
      <label
        htmlFor="covered-cost-center"
        className="text-sm font-medium text-foreground"
      >
        {t("book_location.booking_summary.cost_center")}
      </label>
      <input
        id="covered-cost-center"
        type="text"
        maxLength={COST_CENTER_MAX_LENGTH}
        className="w-full rounded-[5px] border border-foreground/20 bg-transparent px-3 py-2 text-sm text-foreground"
        value={value}
        onChange={(event) =>
          dispatch(costCenterTyped({ costCenter: event.target.value }))
        }
      />
      <p className="text-sm text-foreground/60">
        {t("book_location.booking_summary.cost_center_hint")}
      </p>
    </div>
  );
};

export default CoveredCostCenterField;
